import React, { useMemo, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";

const EMPTY_DRAFT = {
  resolution: "",
  fps: "",
  bitrate: "",
  bitrate_control: "",
  encoding: "",
};

function SupportBadge({ supported, total }) {
  if (total == null || total === 0) return null;
  const allSupported = supported === total;
  const noneSupported = supported === 0;
  const colorClass = allSupported ? "text-success" : noneSupported ? "text-danger" : "text-warning";
  return (
    <small className={`${colorClass} d-block`}>
      <i
        className={`fas mr-1 ${
          allSupported ? "fa-check-circle" : noneSupported ? "fa-times-circle" : "fa-exclamation-circle"
        }`}
      />
      Supported by {supported}/{total} channels
    </small>
  );
}

function FieldRow({ label, control, infoContent }) {
  return (
    <div className="mb-3">
      <Row className="align-items-end">
        <Col md="7">
          <Label className="text-uppercase small mb-1">{label}</Label>
          {control}
        </Col>
        <Col md="5" className="pb-1">
          {infoContent}
        </Col>
      </Row>
    </div>
  );
}

function BulkConfigPanel({ displayRows, channelRows, onApply, applying, applyReport }) {
  const [selectedChannels, setSelectedChannels] = useState(new Set());
  const [streamType, setStreamType] = useState("main");
  const [bulkDraft, setBulkDraft] = useState(EMPTY_DRAFT);

  const channelOptions = useMemo(() => {
    if (!Array.isArray(displayRows)) return [];
    return displayRows.map((row) => ({
      channel: row.channel,
      label: row.channel_name ? `Channel ${row.channel} — ${row.channel_name}` : `Channel ${row.channel}`,
    }));
  }, [displayRows]);

  const capsOptions = useMemo(() => {
    const resolutions = new Set();
    const fpsList = new Set();
    const modes = new Set();
    const encodings = new Set();
    let bitrateMin = Infinity;
    let bitrateMax = -Infinity;

    if (Array.isArray(channelRows)) {
      channelRows.forEach((row) => {
        const stream = streamType === "main" ? row.main : row.sub;
        if (!stream) return;
        (stream.supported_resolutions || []).forEach((v) => resolutions.add(v));
        (stream.supported_fps || []).forEach((v) => fpsList.add(String(v)));
        (stream.supported_bitrate_control_modes || []).forEach((v) => modes.add(String(v).toLowerCase()));
        (stream.supported_encodings || []).forEach((v) => {
          const s = v != null ? String(v).trim() : "";
          if (s) encodings.add(s);
        });
        if (Number.isFinite(stream.min_bitrate) && stream.min_bitrate < bitrateMin) bitrateMin = stream.min_bitrate;
        if (Number.isFinite(stream.max_bitrate) && stream.max_bitrate > bitrateMax) bitrateMax = stream.max_bitrate;
      });
    }

    return {
      resolutions: [...resolutions],
      fpsList: [...fpsList].sort((a, b) => Number(a) - Number(b)),
      modes: [...modes],
      encodings: [...encodings],
      bitrateRange:
        Number.isFinite(bitrateMin) && Number.isFinite(bitrateMax) ? { min: bitrateMin, max: bitrateMax } : null,
    };
  }, [channelRows, streamType]);

  const supportCounts = useMemo(() => {
    const channelsToCheck = selectedChannels.size > 0 ? [...selectedChannels] : [];
    if (!Array.isArray(channelRows) || channelsToCheck.length === 0) return {};
    const total = channelsToCheck.length;
    const count = (predicate) =>
      channelsToCheck.filter((ch) => {
        const row = channelRows.find((r) => r.channel === ch);
        const stream = streamType === "main" ? row?.main : row?.sub;
        return stream ? predicate(stream) : false;
      }).length;
    return {
      resolution: { supported: count((s) => (s.supported_resolutions || []).length > 0), total },
      fps: { supported: count((s) => (s.supported_fps || []).length > 0), total },
      bitrate: { supported: count((s) => Number.isFinite(s.min_bitrate) && Number.isFinite(s.max_bitrate)), total },
      bitrate_control: { supported: count((s) => (s.supported_bitrate_control_modes || []).length > 0), total },
      encoding: { supported: count((s) => (s.supported_encodings || []).length > 0), total },
    };
  }, [channelRows, selectedChannels, streamType]);

  const toggleChannel = (channel) => {
    setSelectedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) next.delete(channel);
      else next.add(channel);
      return next;
    });
  };

  const setField = (field, value) => setBulkDraft((prev) => ({ ...prev, [field]: value }));

  const handleReset = () => setBulkDraft(EMPTY_DRAFT);

  const selectedCount = selectedChannels.size;
  const hasAnyValue = Object.values(bulkDraft).some((v) => v !== "");
  const canApply = selectedCount > 0 && hasAnyValue && !applying;

  const hasCaps = Array.isArray(channelRows) && channelRows.length > 0;
  const { resolutions, fpsList, modes, encodings, bitrateRange } = capsOptions;
  const noCapsInfo = <small className="text-muted d-block">Get DVR capabilities first</small>;
  const notAdvertisedInfo = <small className="text-muted d-block">Not advertised by device</small>;

  return (
    <div className="border rounded p-3 mb-3">
      <Row>
        <Col md="4" style={{ borderRight: "1px solid rgba(255,255,255,0.12)" }}>
          <h6 className="text-uppercase small mb-3">1. Select channels</h6>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">{selectedCount} selected</small>
            <div>
              <Button
                size="sm"
                color="link"
                className="p-0 mr-2"
                onClick={() => setSelectedChannels(new Set(channelOptions.map((o) => o.channel)))}
              >
                Select all
              </Button>
              <Button size="sm" color="link" className="p-0" onClick={() => setSelectedChannels(new Set())}>
                None
              </Button>
            </div>
          </div>
          <div style={{ maxHeight: "360px", overflowY: "auto" }}>
            {channelOptions.length === 0 ? (
              <p className="small text-muted">No channels available.</p>
            ) : (
              channelOptions.map((opt) => (
                <FormGroup check key={opt.channel} className="mb-1">
                  <Input
                    type="checkbox"
                    id={`bulk-ch-${opt.channel}`}
                    checked={selectedChannels.has(opt.channel)}
                    onChange={() => toggleChannel(opt.channel)}
                  />
                  <Label check htmlFor={`bulk-ch-${opt.channel}`} className="small">
                    {opt.label}
                  </Label>
                </FormGroup>
              ))
            )}
          </div>
        </Col>

        <Col md="8" className="pl-4">
          <h6 className="text-uppercase small mb-3">2. Configure selected channels</h6>

          <FieldRow
            label="Stream"
            control={
              <Input
                type="select"
                bsSize="sm"
                value={streamType}
                onChange={(e) => {
                  setStreamType(e.target.value);
                  setBulkDraft(EMPTY_DRAFT);
                }}
              >
                <option value="main">Main stream</option>
                <option value="sub">Sub stream</option>
              </Input>
            }
            infoContent={null}
          />

          <FieldRow
            label="Resolution"
            control={
              hasCaps && resolutions.length > 0 ? (
                <Input
                  type="select"
                  bsSize="sm"
                  value={bulkDraft.resolution}
                  onChange={(e) => setField("resolution", e.target.value)}
                >
                  <option value="">— unchanged —</option>
                  {resolutions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Input>
              ) : (
                <Input type="select" bsSize="sm" disabled>
                  <option value="">—</option>
                </Input>
              )
            }
            infoContent={
              hasCaps ? (
                resolutions.length > 0 ? (
                  <SupportBadge {...(supportCounts.resolution || {})} />
                ) : (
                  notAdvertisedInfo
                )
              ) : (
                noCapsInfo
              )
            }
          />

          <FieldRow
            label="FPS"
            control={
              hasCaps && fpsList.length > 0 ? (
                <Input
                  type="select"
                  bsSize="sm"
                  value={bulkDraft.fps}
                  onChange={(e) => setField("fps", e.target.value)}
                >
                  <option value="">— unchanged —</option>
                  {fpsList.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Input>
              ) : (
                <Input type="select" bsSize="sm" disabled>
                  <option value="">—</option>
                </Input>
              )
            }
            infoContent={
              hasCaps ? (
                fpsList.length > 0 ? (
                  <SupportBadge {...(supportCounts.fps || {})} />
                ) : (
                  notAdvertisedInfo
                )
              ) : (
                noCapsInfo
              )
            }
          />

          <FieldRow
            label="Bitrate (kbps)"
            control={
              hasCaps && bitrateRange ? (
                <Input
                  type="number"
                  bsSize="sm"
                  min={bitrateRange.min}
                  max={bitrateRange.max}
                  step={1}
                  placeholder="— unchanged —"
                  value={bulkDraft.bitrate}
                  onChange={(e) => setField("bitrate", e.target.value)}
                />
              ) : (
                <Input type="select" bsSize="sm" disabled>
                  <option value="">—</option>
                </Input>
              )
            }
            infoContent={
              hasCaps ? (
                bitrateRange ? (
                  <>
                    <SupportBadge {...(supportCounts.bitrate || {})} />
                    <small className="text-muted d-block">
                      {bitrateRange.min} – {bitrateRange.max} kbps
                    </small>
                  </>
                ) : (
                  notAdvertisedInfo
                )
              ) : (
                noCapsInfo
              )
            }
          />

          <FieldRow
            label="Bitrate control"
            control={
              hasCaps && modes.length > 0 ? (
                <Input
                  type="select"
                  bsSize="sm"
                  value={bulkDraft.bitrate_control}
                  onChange={(e) => setField("bitrate_control", e.target.value)}
                >
                  <option value="">— unchanged —</option>
                  {modes.map((m) => (
                    <option key={m} value={m}>
                      {m.toUpperCase()}
                    </option>
                  ))}
                </Input>
              ) : (
                <Input type="select" bsSize="sm" disabled>
                  <option value="">—</option>
                </Input>
              )
            }
            infoContent={
              hasCaps ? (
                modes.length > 0 ? (
                  <SupportBadge {...(supportCounts.bitrate_control || {})} />
                ) : (
                  notAdvertisedInfo
                )
              ) : (
                noCapsInfo
              )
            }
          />

          <FieldRow
            label="Encoding"
            control={
              hasCaps && encodings.length > 0 ? (
                <Input
                  type="select"
                  bsSize="sm"
                  value={bulkDraft.encoding}
                  onChange={(e) => setField("encoding", e.target.value)}
                >
                  <option value="">— unchanged —</option>
                  {encodings.map((enc) => (
                    <option key={enc} value={enc}>
                      {enc}
                    </option>
                  ))}
                </Input>
              ) : (
                <Input type="select" bsSize="sm" disabled>
                  <option value="">—</option>
                </Input>
              )
            }
            infoContent={
              hasCaps ? (
                encodings.length > 0 ? (
                  <SupportBadge {...(supportCounts.encoding || {})} />
                ) : (
                  notAdvertisedInfo
                )
              ) : (
                noCapsInfo
              )
            }
          />

          <small className="text-muted d-block mb-3">
            Empty fields are left unchanged. Settings may fail for channels without matching capabilities.
          </small>

          <div className="d-flex" style={{ gap: "0.5rem" }}>
            <Button
              color="success"
              disabled={!canApply}
              onClick={() => onApply([...selectedChannels], streamType, bulkDraft)}
            >
              {applying && <i className="fas fa-spinner fa-spin mr-2" />}
              Apply to {selectedCount} channel{selectedCount !== 1 ? "s" : ""}
            </Button>
            <Button color="secondary" outline disabled={applying} onClick={handleReset}>
              Reset
            </Button>
          </div>
          {applyReport && (
            <div className="mt-3">
              {applyReport.complete.length > 0 && (
                <small className="text-success d-block">
                  <i className="fas fa-check-circle mr-1" />
                  Fully updated: Channel{applyReport.complete.length !== 1 ? "s" : ""} {applyReport.complete.join(", ")}
                </small>
              )}
              {applyReport.partial.length > 0 && (
                <small className="text-warning d-block">
                  <i className="fas fa-exclamation-circle mr-1" />
                  Partially updated: Channel{applyReport.partial.length !== 1 ? "s" : ""}{" "}
                  {applyReport.partial.join(", ")}
                </small>
              )}
              {applyReport.failed.length > 0 && (
                <small className="text-danger d-block">
                  <i className="fas fa-times-circle mr-1" />
                  Not updated: Channel{applyReport.failed.length !== 1 ? "s" : ""} {applyReport.failed.join(", ")}
                </small>
              )}
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default BulkConfigPanel;
